import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  Box,
  Button,
  Header,
  Icon,
  Modal,
  ModalProps,
  Popover,
  SpaceBetween,
  MultiselectProps,
} from '@amzn/awsui-components-react-v3';
import { CancelableEventHandler, ClickDetail } from '@amzn/awsui-components-react-v3/polaris/internal/events';

import { logger, useGetUserInformation } from 'appSelectorContext';
import { isEmpty, isNull, isObject, omitBy, uniqueId } from 'lodash';
import { remainingQtyFromModel } from 'src/components/MPNsToReceive/ColumnDefinitions/Quantities/RemainingQuanity';
import { v4 as uuidv4 } from 'uuid';

import { ModelDefinitions } from '../components/MPNsToReceive/ColumnDefinitions/ModelDefinitions';
import { MPNsToReceiveStatus, UnExpectedTagsWarning } from '../components/MPNsToReceive/MPNsToReceiveStatus';
import ReceivePartsAction, { useReceiveParts } from '../components/ReceivePartsAction/';
import ScanContainer from '../components/ScanContainer';
import ScanSerials from '../components/ScanSerials';
import { Context, Types } from '../store';
import {
  ActiveContainerType,
  AsyncState,
  ContainerReceiveError,
  ContainerValidationError,
  Model,
  ReceiveErrors,
  ReceiveType,
  ReferenceLineItemValidationCode,
  ReferenceQueryResponseItem,
  Serial,
  ValidationStatusType,
} from '../store/types';

interface ContainerPageProps {
  setModelId: (id: string | null) => void;
}

interface ResetScansProps {
  resetKey: string;
  onHandleCancelResetScans: () => void;
  onHandleConfirmResetScans: () => void;
  disabled: boolean;
  show: boolean;
}

interface ReceiveActionsProps extends Omit<ResetScansProps, 'disabled'> {
  onHandleCloseModal: CancelableEventHandler<ClickDetail> | undefined;
  showResetScans: boolean;
  model: Model;
  underage: boolean;
  isResetScansDisabled: boolean;
  overage: boolean;
  shouldDisableButton: boolean;
  serials: Serial[];
  onReceiveParts: () => void;
  isCloseDisabled: boolean;
}

const ReceiveActions = memo(
  ({
    onHandleCloseModal,
    isCloseDisabled,
    resetKey,
    onHandleCancelResetScans,
    onHandleConfirmResetScans,
    isResetScansDisabled,
    showResetScans,
    model,
    underage,
    overage,
    shouldDisableButton,
    serials,
    onReceiveParts,
    show = true,
  }: ReceiveActionsProps) => {
    if (!show) {
      return (
        <div className="receive-action">
          <Button variant="link" onClick={onHandleCloseModal}>
            Cancel
          </Button>
        </div>
      );
    }

    return (
      <div className="receive-action">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={onHandleCloseModal}>
            Cancel
          </Button>
          <ResetScans
            resetKey={resetKey}
            onHandleCancelResetScans={onHandleCancelResetScans}
            onHandleConfirmResetScans={onHandleConfirmResetScans}
            disabled={isResetScansDisabled}
            show={showResetScans}
          />
          <ReceivePartsAction
            model={model}
            underage={underage}
            overrage={overage}
            disabled={shouldDisableButton}
            allSerials={serials}
            onReceiveParts={onReceiveParts}
          />
        </SpaceBetween>
      </div>
    );
  },
);

const ResetScans = memo(
  ({ resetKey, onHandleCancelResetScans, onHandleConfirmResetScans, disabled, show = true }: ResetScansProps) => {
    if (!show) {
      return null;
    }

    return (
      <Popover
        key={resetKey}
        dismissButton={true}
        position="top"
        size="large"
        triggerType="custom"
        header="Confirm Reset"
        content={
          <Box>
            <p>Are you sure you want to reset scans? All the parts scanned inside this model will be lost</p>
            <div className="confirm-actions">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={onHandleCancelResetScans}>
                  Cancel
                </Button>
                <Button variant="link" onClick={onHandleConfirmResetScans}>
                  <SpaceBetween direction="horizontal" size="xs">
                    <Icon name="remove" variant="error" />
                    <Box variant="span" color="text-status-error">
                      Reset
                    </Box>
                  </SpaceBetween>
                </Button>
              </SpaceBetween>
            </div>
          </Box>
        }
      >
        <Button variant="link" disabled={disabled}>
          Reset Scans
        </Button>
      </Popover>
    );
  },
);

export function ContainerPage({ setModelId }: ContainerPageProps) {
  const { referenceId, modelId, receiveType }: any = useParams();
  const isAdjustment = receiveType === ReceiveType.INVENTORY_ADJUSTMENT;
  const isRMA = receiveType === 'no_reference';

  const { state, dispatch } = useContext(Context);
  const history = useHistory();
  const [resetKey, setResetKey] = useState(uniqueId());
  const [request, setRequest] = useState(null);
  const model: Model = state?.models?.find((m: Model) => m?.mpnIpnId === modelId && m?.referenceId === referenceId);
  const [tags, setTags] = React.useState<MultiselectProps.Option[]>([]);
  const [buildId, setBuildId] = useState<string | undefined>(model?.buildId);
  const selectedCategory =
    (isRMA || isAdjustment) && isEmpty(model?.buildCategory) ? 'DCO_SPARE' : model?.buildCategory;
  const [updatingContainer, setUpdatingContainer] = useState(false);
  const [activeContainer, setActiveContainer] = useState<ActiveContainerType | null>(null);
  const [unExpectedTags, setUnExpectedTags] = useState<any>([]);
  const [isEmptyPOLinesWarningAcknowledged, setIsEmptyPOLinesWarningAcknowledged] = useState<boolean>(false);
  const isRequireManualPOReceiveWarning =
    model?.referenceLineItemStatus?.validationStatusType === ValidationStatusType.WARNING &&
    model?.referenceLineItemStatus?.statusCode === ReferenceLineItemValidationCode.REQUIRE_MANUAL_PO_RECEIVE;
  const context = useGetUserInformation();
  const onHandleCancelResetScans = () => setResetKey(uniqueId());
  const executionReceiveType = useMemo(() => {
    if (isAdjustment) {
      return ReceiveType.INVENTORY_ADJUSTMENT.toUpperCase();
    }
    if (isRMA) {
      return 'RMA';
    }
    return 'PRECONDITION_RECEIVE';
  }, [isAdjustment, isRMA]);

  const serials: Serial[] = state?.serials?.filter(
    (serial: Serial) =>
      serial.containerId?.toUpperCase() === activeContainer?.value?.toUpperCase() && serial.mpnIpnId === modelId,
  );
  const allSerialsInMPN: Serial[] = state?.serials?.filter((serial: Serial) => serial.mpnIpnId === modelId);

  const allSerialsinModelNoErrors: Serial[] = state?.serials?.filter(
    (serial: Serial) =>
      serial.mpnIpnId === modelId &&
      serial.state !== AsyncState.ERROR &&
      serial.state !== AsyncState.IN_PROGRESS &&
      !serial.loading,
  );
  const allSerialsinModelErrors: Serial = state?.serials?.find(
    (serial: Serial) =>
      serial.mpnIpnId === modelId && serial.status?.validationStatusType === ValidationStatusType.ERROR,
  );
  const pendingSerials: Serial = state?.serials?.find(
    (serial: Serial) => serial.mpnIpnId === modelId && serial.state === AsyncState.IN_PROGRESS,
  );

  const underage = allSerialsinModelNoErrors.length < (model?.maximumExpectedQuantity || 0);
  const expectedQty = model?.maximumExpectedQuantity || 0;
  const currentReceivedQty = model?.currentReceivedQuantity || 0;
  const overrage = currentReceivedQty + allSerialsinModelNoErrors.length > expectedQty;
  const containers = state?.containers;
  const nextSerialOverage = currentReceivedQty + allSerialsInMPN.length > expectedQty - 1;
  const reference: ReferenceQueryResponseItem = state?.receiveReferences?.find(
    (ref: ReferenceQueryResponseItem) => ref?.receiveReference?.receiveReferenceId === referenceId,
  );
  const receivedQty = currentReceivedQty + allSerialsInMPN.length;

  const receivedErrorsList: ReceiveErrors[] = model?.receivedErrorsList || [];
  const containersErrors: ContainerReceiveError[] = receivedErrorsList.filter((e) => isObject(e));
  const invalidContainers = containersErrors
    .filter((c: ContainerReceiveError) => c.errorCode !== ContainerValidationError.CONTAINER_NOT_IN_USER_CUSTODY)
    .map((c: ContainerReceiveError) => c.id);
  const containersNotinUserCustody = containersErrors
    .filter((c: ContainerReceiveError) => c.errorCode === ContainerValidationError.CONTAINER_NOT_IN_USER_CUSTODY)
    .map((c: ContainerReceiveError) => c.id);

  const genericErrors = model?.receivedErrorsList?.filter((e) => !isObject(e));
  const totalErrors = model?.receivedErrorsList?.length || 0;

  const executionExists = state?.executions?.find((e) => e?.mpnIpnId === model?.mpnIpnId);
  const draftExeucution = useMemo(
    () => ({
      receiveReferenceId: referenceId,
      trackingId: reference?.receiveReference?.trackingId,
      receiveScanStartTime: reference?.scanStartTime,
      receiveScanEndTime: Date.now(),
      clientToken: uuidv4(),
      mpnIpnId: model?.mpnIpnId,
      status: AsyncState.CREATED,
      externalScannableId: reference?.receiveReference?.externalReferenceValue,
      receivedLineItemList: [
        {
          referenceLineItemId: model?.referenceLineItemId,
          manufacturerPartNumber: model?.expectedMPN,
          manufacturer: model?.manufacturer,
          internalPartNumber: model?.mappedIPN,
          materialDefinitionId: model?.expectedMaterialDefinitionId,
          containerizedMaterialList: model?.containerIds?.map((id: string) => {
            const serials: Serial[] = state?.serials?.filter(
              (serial: Serial) =>
                serial.containerId?.toUpperCase() === id?.toUpperCase() && serial.mpnIpnId === model?.mpnIpnId,
            );
            const container = containers?.[id];
            return {
              containerScannableId: id,
              receiveQuantity: serials?.length,
              containerType: container?.type,
              receivedSerialList: [],
            };
          }),
        },
      ],
    }),
    [
      model?.containerIds,
      model?.expectedMPN,
      model?.manufacturer,
      model?.expectedMaterialDefinitionId,
      model?.mappedIPN,
      model?.mpnIpnId,
      model?.referenceLineItemId,
      reference?.receiveReference?.externalReferenceValue,
      reference?.receiveReference?.trackingId,
      reference?.scanStartTime,
      referenceId,
      state?.serials,
      containers,
    ],
  );

  const { receiveParts, loading, error } = useReceiveParts({
    model,
    onCompleted: (data: any) => {
      const receiveExecutionUUId = data?.receiveMaterialAsync?.receiveExecutionUUId;
      const requestObject: any = request || {};
      logger.info(
        `updating execution request: ${JSON.stringify({
          ...requestObject,
          receiveExecutionUUId,
          mpnIpnId: model?.mpnIpnId,
          withUser: context.user,
        })} `,
      );

      dispatch({
        type: Types.updateExecutions,
        payload: { ...requestObject, receiveExecutionUUId, mpnIpnId: model?.mpnIpnId },
      });
    },
  });

  const isValidBuildId = useMemo(() => {
    let isValid = true;
    if (!model?.referenceLineItemId && !selectedCategory) {
      isValid = false;
    }

    if (selectedCategory === 'BUILD' && isEmpty(model.buildId)) {
      isValid = false;
    }

    return isValid;
  }, [selectedCategory, model?.buildId, model?.referenceLineItemId]);

  const processing =
    model?.status === AsyncState.IN_PROGRESS ||
    model?.status === AsyncState.OK ||
    loading ||
    !!model?.receiveExecutionUUId;

  // True iff the receive type is for Janus or AWS-owned inventory, and an overage has occurred.
  const isBlockingOverage: boolean =
    overrage &&
    (model?.externalReferenceType === ReceiveType.ASN_AWS_OWNED_INVENTORY_RO ||
      model?.externalReferenceType === ReceiveType.ASN_JANUS);

  const shouldDisableButton =
    isBlockingOverage ||
    unExpectedTags.length > 0 ||
    allSerialsinModelNoErrors.length === 0 ||
    !!allSerialsinModelErrors ||
    pendingSerials ||
    model?.status === AsyncState.OK ||
    model?.status === AsyncState.IN_PROGRESS ||
    !isEmptyPOLinesWarningAcknowledged ||
    loading ||
    !!model?.receiveExecutionUUId ||
    !isValidBuildId;

  const redirectToReceivePage = () => {
    history.push(`/receive/${receiveType}/${referenceId}`);
  };

  const onHandleCloseModal = ({ detail }: { detail: ModalProps.DismissDetail }) => {
    if (detail.reason === 'keyboard' || detail.reason === 'overlay') {
      return;
    }
    redirectToReceivePage();
  };

  const onReceiveParts = () => {
    const nonEmptyContainers = model.containerIds?.filter((id: string) => {
      return !!state?.serials?.find(
        (serial: Serial) => serial.containerId?.toUpperCase() === id?.toUpperCase() && serial.mpnIpnId === modelId,
      );
    });
    const siteName = reference.receiveReference.siteName || '';
    const clusterName = siteName ? siteName?.slice(0, 3) : '';
    const selectedSiteName = siteName.trim().length === 0 ? undefined : siteName;
    const containers = state?.containers;
    const buildId = isAdjustment || isRMA || isEmpty(model.referenceLineItemId) ? model.buildId : undefined;
    const receivingLineItemList = [
      {
        referenceLineItemId: model.referenceLineItemId,
        manufacturerPartNumber: model?.expectedMPN,
        manufacturer: model?.manufacturer,
        internalPartNumber: model.mappedIPN,
        materialDefinitionId: model.expectedMaterialDefinitionId,
        lineItemMetricList: model?.metricsList || [],
        tagList: tags.map((option) => option.value),
        containerizedMaterialList: nonEmptyContainers?.map((id: string) => {
          const serials: Serial[] = state?.serials?.filter(
            (serial: Serial) => serial.containerId?.toUpperCase() === id?.toUpperCase() && serial.mpnIpnId === modelId,
          );
          const container = containers?.[id];
          return {
            containerScannableId: id,
            containerType: container?.type,
            receiveQuantity: serials.length,
            receivedSerialList: serials.map((s: Serial) => ({
              externalSerialId: s.serialNumber,
              siteSpecificSerialId: s.siteSpecificSerialId,
              serialMetrics: s?.metricsList || [],
            })),
          };
        }),
      },
    ];
    const request = {
      receiveReferenceId: isAdjustment ? 'no_reference' : referenceId,
      trackingId: isAdjustment ? '' : reference.receiveReference.trackingId || '',
      receiveScanStartTime: isAdjustment ? Date.now() : reference.scanStartTime,
      receiveScanEndTime: Date.now(),
      clientToken: uuidv4(),
      externalScannableId: reference.receiveReference.externalReferenceValue,
      receiveMethodUserProvidedReason: isAdjustment ? undefined : reference.shipmentMissingReasonCode || '',
      siteName: !isEmpty(model.siteName) ? model.siteName : selectedSiteName,
      category: isAdjustment || isRMA || isEmpty(model.referenceLineItemId) ? selectedCategory : undefined,
      receiveType: executionReceiveType,
      inventoryAdjustmentUsageReason: isAdjustment ? model.receiveReason : undefined,
      clusterName: !isEmpty(model.siteName) ? model?.siteName?.slice(0, 3) : clusterName,
      siteEquipmentId: reference.receiveReference.expectedReceiveLocation,
      receivedBy: context?.user,
      receivedLineItemList: receivingLineItemList.map((receivingLineItem) => omitBy(receivingLineItem, isNull)),
    };

    if (!isEmpty(buildId)) {
      request.buildId = buildId;
    }

    if (isRMA) {
      request.rmaId = reference.receiveReference.externalReferenceValue;
    }

    setRequest(request);
    receiveParts({
      variables: request,
    });
  };

  const onHandleResetModel = () => {
    dispatch({
      type: Types.resetScans,
      payload: { mpnIpnId: model.mpnIpnId },
    });
    setActiveContainer(null);
    onHandleCancelResetScans();
  };

  const handleBuildIdChange = (build: string) => {
    setBuildId(build);
    dispatch({
      type: Types.updateBuildID,
      payload: {
        buildId: build,
        id: model.mpnIpnId,
      },
    });
  };

  const handleCategoryChange = (selectedCategory: any) => {
    dispatch({
      type: Types.updateCategory,
      payload: {
        category: selectedCategory?.value,
        id: model.mpnIpnId,
      },
    });
  };

  useEffect(() => {
    setModelId(modelId);
    return () => setModelId(null);
  }, [modelId, setModelId]);

  useEffect(() => {
    if (!isEmpty(model?.buildId)) {
      dispatch({
        type: Types.updateBuildID,
        payload: {
          buildId: model?.buildId,
          id: model.mpnIpnId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!model) {
      console.error(`Reference ID: ${referenceId} was not found in the model`);
      history.push(`/receive/${receiveType}/${referenceId}`);
    }
  }, [model, history, receiveType, referenceId]);

  useEffect(() => {
    // if not exists create a draft
    if (!executionExists && draftExeucution) {
      logger.info(`adding to execution: ${JSON.stringify({ ...draftExeucution, withUser: context.user })} `);
      dispatch({
        type: Types.addToExecutions,
        payload: { ...draftExeucution, receiveScanEndTime: Date.now() },
      });
    }
  }, [draftExeucution, dispatch, executionExists, context]);

  // Set isEmptyPOLinesWarningAcknowledged to true
  // for conditions other than referenceLineItemStatus being REQUIRE_MANUAL_PO_RECEIVE

  useEffect(() => {
    if (
      !(
        model?.referenceLineItemStatus?.validationStatusType === ValidationStatusType.WARNING &&
        model?.referenceLineItemStatus?.statusCode === ReferenceLineItemValidationCode.REQUIRE_MANUAL_PO_RECEIVE
      )
    ) {
      setIsEmptyPOLinesWarningAcknowledged(true);
    }
  }, [model?.referenceLineItemStatus]);

  const renderContainerBody = () => {
    return (
      <Box>
        {<UnExpectedTagsWarning unExpectedTags={unExpectedTags} />}
        <Header variant="h1" description="Scan parts into containers">
          Scan Parts
        </Header>
        <hr />
        <SpaceBetween size="l">
          <ModelDefinitions
            model={model}
            isAdjustment={isAdjustment}
            isRMA={isRMA}
            processing={processing}
            receivingQty={allSerialsinModelNoErrors.length}
            receivedQty={receivedQty}
            remainingQty={remainingQtyFromModel(model, state?.serials || [])}
            tags={tags}
            unExpectedTags={unExpectedTags}
            setUnExpectedTags={setUnExpectedTags}
            setTags={setTags}
            selectedCategory={selectedCategory}
            handleCategoryChange={handleCategoryChange}
            handleBuildIdChange={handleBuildIdChange}
            buildId={buildId}
          />
          <MPNsToReceiveStatus
            status={model?.status}
            error={error}
            genericErrors={genericErrors}
            invalidContainers={invalidContainers}
            containersNotInUserCustody={containersNotinUserCustody}
            totalErrors={totalErrors}
            isAdjustment={isAdjustment}
            isRMA={isRMA}
            overrage={overrage}
            setIsEmptyPOLinesWarningAcknowledged={setIsEmptyPOLinesWarningAcknowledged}
            isEmptyPOLinesWarningAcknowledged={isEmptyPOLinesWarningAcknowledged}
            isRequireManualPOReceiveWarning={isRequireManualPOReceiveWarning}
            currentReceivedQty={currentReceivedQty}
            isBlockingOverage={isBlockingOverage}
            receivingQty={allSerialsinModelNoErrors.length}
            expectedQty={expectedQty}
          />
          <hr />
          <ScanContainer
            activeContainer={activeContainer}
            referenceId={referenceId}
            setActiveContainer={(val) => {
              setActiveContainer(val);
            }}
            setUpdatingContainer={setUpdatingContainer}
            processing={processing}
          />
          <ScanSerials
            serials={serials}
            activeContainer={activeContainer}
            modelId={modelId}
            referenceId={referenceId}
            disable={updatingContainer}
            overrage={nextSerialOverage}
            processing={processing}
            show={!!activeContainer && !!model && !updatingContainer}
          />
          <hr />
        </SpaceBetween>
        <ReceiveActions
          show={!model?.receiveExecutionUUId}
          onHandleCloseModal={redirectToReceivePage}
          showResetScans={allSerialsInMPN.length > 0}
          model={model}
          underage={underage}
          isResetScansDisabled={model?.status === AsyncState.OK || model?.status === AsyncState.IN_PROGRESS || loading}
          overage={overrage}
          shouldDisableButton={shouldDisableButton}
          serials={allSerialsinModelNoErrors}
          onReceiveParts={onReceiveParts}
          isCloseDisabled={[AsyncState.IN_PROGRESS, AsyncState.OK, AsyncState.ERROR].includes(model?.status) || loading}
          resetKey={resetKey}
          onHandleCancelResetScans={onHandleCancelResetScans}
          onHandleConfirmResetScans={onHandleResetModel}
        />
      </Box>
    );
  };

  return (
    <Modal
      onDismiss={onHandleCloseModal}
      visible={true}
      closeAriaLabel="Close modal"
      size="max"
      footer={null}
      header={null}
    >
      {renderContainerBody()}
    </Modal>
  );
}
